<script setup lang="ts">
import { faImage, faXmark } from '@fortawesome/pro-light-svg-icons';
import { FontAwesomeIcon, TmlButton, TmlClickHandler, TmlImage } from '@teemill/components';
import type { Notification } from '@teemill/dashboard-user';
import { useSwipe } from '@vueuse/core';
import dayjs from 'dayjs';
import relativeTime from 'dayjs/plugin/relativeTime';
import { computed, ref } from 'vue';

dayjs.extend(relativeTime);

const props = defineProps<{
  notification: Notification;
}>();

const emit = defineEmits<{
  (e: 'remove'): void;
  (e: 'click', preventClose: boolean): void;
}>();

const decodedAction = computed(() => {
  return decodeURIComponent(props.notification.action);
});

const swipeThreshold = 200;
const minOpacity = 0.3;
const scrollDetectionThreshold = 10;
const swipeReturnDuration = 0.3;

const swipable = ref<HTMLElement | null>(null);
const isScrolling = ref(false);
let initialTouchX = 0;
let initialTouchY = 0;

const handleTouchStart = (e: TouchEvent) => {
  isScrolling.value = false;
  if (e.touches[0]) {
    initialTouchY = e.touches[0].clientY;
    initialTouchX = e.touches[0].clientX;
  }
};

const handleTouchMove = (e: TouchEvent) => {
  if (e.touches[0]) {
    const deltaY = Math.abs(e.touches[0].clientY - initialTouchY);
    const deltaX = Math.abs(e.touches[0].clientX - initialTouchX);

    if (deltaY > deltaX && deltaY > scrollDetectionThreshold) {
      isScrolling.value = true;
      if (swipable.value) {
        swipable.value.style.transform = 'translateX(0)';
        swipable.value.style.opacity = '1';
      }
    }
  }
};

const { direction, lengthX } = useSwipe(swipable, {
  threshold: 0,
  onSwipe() {
    if (!swipable.value || isScrolling.value) {
      return;
    }

    swipable.value.style.transition = 'none';
    const movement =
      direction.value === 'left' ? -Math.abs(lengthX.value) : Math.abs(lengthX.value);
    const opacity = Math.max(1 - Math.abs(lengthX.value) / swipeThreshold, minOpacity);
    swipable.value.style.transform = `translateX(${movement}px)`;
    swipable.value.style.opacity = opacity.toString();
  },
  onSwipeEnd() {
    if (!swipable.value || isScrolling.value) {
      return;
    }

    if (Math.abs(lengthX.value) > swipeThreshold) {
      emit('remove');
    } else {
      swipable.value.style.transition = `all ${swipeReturnDuration}s ease`;
      swipable.value.style.transform = 'translateX(0)';
      swipable.value.style.opacity = '1';
    }
  },
});
</script>

<template>
  <div
    ref="swipable"
    class="block touch-pan-y touch-pinch-zoom will-change-transform hover:bg-[--tml-card-hover-background-color]"
    @touchstart="handleTouchStart"
    @touchmove="handleTouchMove"
  >
    <TmlClickHandler
      :to="decodedAction"
      @click="emit('click', false)"
      @click-middle="emit('click', true)"
    >
      <div class="p-3 flex items-start gap-3">
        <div
          class="shrink-0 relative w-16 h-16 rounded-[--tml-inner-radius] overflow-hidden bg-[--tml-card-background-color]"
        >
          <TmlImage
            v-if="notification.image"
            :src="notification.image"
            fit="contain"
            :alt="notification.title"
          />
          <div v-else class="w-16 h-16 flex items-center justify-center">
            <FontAwesomeIcon :icon="faImage" />
          </div>
        </div>
        <div class="flex-grow flex flex-col overflow-hidden">
          <h5 class="truncate font-bold">{{ notification.title }}</h5>
          <p class="text-sm line-clamp-2 text-pretty">
            {{ notification.body }}
          </p>
          <p class="text-sm" :class="{ 'text-[--tml-primary-color]': !notification.readAt }">
            {{ dayjs(notification.createdAt).fromNow() }}
          </p>
        </div>
        <TmlButton class="touch-target p-3 -m-3" @click.stop.prevent="emit('remove')">
          <FontAwesomeIcon :icon="faXmark" />
        </TmlButton>
      </div>
    </TmlClickHandler>
  </div>
</template>
